import type { ReactNode } from 'react';

import { cx } from './cx';
import type { Tone } from './Pill';

/**
 * The third view's building blocks. These only draw: every percentage on them is worked out
 * by `src/timeline/layout.ts`, which is a pure module precisely so that the picture can be
 * tested without a browser.
 *
 * A 200px label column and a track that fills the rest.
 */
export function Timeline({ className, children }: { className?: string; children?: ReactNode }) {
  return (
    <div className={cx('grid grid-cols-[200px_minmax(0,1fr)] text-secondary', className)}>
      {children}
    </div>
  );
}

/** A row's left-hand cell: who or what the row is about. */
export function Lane({
  label,
  aside,
  className,
}: {
  label: ReactNode;
  aside?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'flex items-center justify-between gap-2 h-10 px-3.5 border-b border-r border-rule whitespace-nowrap overflow-hidden',
        className,
      )}
    >
      {label}
      {aside}
    </div>
  );
}

/**
 * A row's right-hand cell: the two hours themselves. The past is shaded, the quarter hours
 * are hairlines, and `now` is a 2px brand rule drawn on every track — so the eye can run a
 * vertical line down the whole fleet and see who is on what at this second.
 */
export function Track({
  nowPct,
  ticks,
  className,
  children,
}: {
  nowPct: number;
  /** The quarter-hour percentages, from the layout module. */
  ticks: number[];
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cx('relative h-10 border-b border-rule', className)}>
      <span
        className="absolute inset-y-0 left-0 bg-surface-2 opacity-55"
        style={{ width: `${nowPct}%` }}
      />
      {ticks.map((pct) => (
        <span key={pct} className="absolute inset-y-0 w-px bg-rule" style={{ left: `${pct}%` }} />
      ))}
      <span className="absolute inset-y-0 w-0.5 -ml-px bg-brand" style={{ left: `${nowPct}%` }} />
      {children}
    </div>
  );
}

/** The clock along the top: a tick every fifteen minutes, on the quarter hour. */
export function Axis({
  nowPct,
  ticks,
  className,
}: {
  nowPct: number;
  ticks: { pct: number; label: string }[];
  className?: string;
}) {
  return (
    <div className={cx('relative h-7 border-b border-rule', className)}>
      <span
        className="absolute inset-y-0 left-0 bg-surface-2 opacity-55"
        style={{ width: `${nowPct}%` }}
      />
      {ticks.map((tick) => (
        <span
          key={tick.pct}
          className="absolute top-[7px] -translate-x-1/2 text-caps text-ink-3"
          style={{ left: `${tick.pct}%` }}
        >
          {tick.label}
        </span>
      ))}
    </div>
  );
}

/**
 * What a driver is on, or what an order is waiting through.
 *
 * The text inside a bar is `ink` rather than the tone: a bar is already the tone, and a
 * green label on a green wash is a label nobody reads. `hollow` is a leg that has not
 * happened yet — the drive to a pickup — drawn as an outline with nothing in it.
 */
export type BarTone = Exclude<Tone, 'neutral' | 'info'> | 'break';

const FILL: Record<BarTone, string> = {
  ok: 'bg-ok-soft border-ok text-ink',
  warn: 'bg-warn-soft border-warn text-ink',
  danger: 'bg-danger-soft border-danger text-ink',
  brand: 'bg-brand-soft border-brand text-ink',
  break: 'bg-surface-3 border-transparent text-ink-3',
};

const OUTLINE: Record<BarTone, string> = {
  ok: 'bg-transparent border-ok text-ink-2',
  warn: 'bg-transparent border-warn text-ink-2',
  danger: 'bg-transparent border-danger text-ink-2',
  brand: 'bg-transparent border-brand text-ink-2',
  break: 'bg-transparent border-rule text-ink-3',
};

export function Bar({
  from,
  to,
  tone,
  dashed,
  hollow,
  title,
  onClick,
  className,
  children,
}: {
  /** Percentages of the track, already clamped to the window by the layout module. */
  from: number;
  to: number;
  tone: BarTone;
  dashed?: boolean;
  hollow?: boolean;
  title?: string;
  onClick?: () => void;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={!onClick}
      className={cx(
        'absolute top-2 h-6 rounded-control text-[12px] flex items-center px-2 whitespace-nowrap overflow-hidden border',
        dashed ? 'border-dashed' : 'border-solid',
        onClick ? 'cursor-pointer' : 'cursor-default',
        hollow ? OUTLINE[tone] : FILL[tone],
        className,
      )}
      style={{ left: `${from}%`, width: `${Math.max(to - from, 0.6)}%` }}
    >
      {children}
    </button>
  );
}

/**
 * The dotted run-up to a pre-booking: from the moment the search will start to the pickup
 * itself. A line rather than a bar, because nothing is happening during it — it is the wait
 * before the firm starts looking.
 */
export function Lead({ from, to, title }: { from: number; to: number; title?: string }) {
  return (
    <span
      title={title}
      className="absolute top-[19px] h-0 border-t-2 border-dotted border-brand"
      style={{ left: `${from}%`, width: `${Math.max(to - from, 0.4)}%` }}
    />
  );
}

/** What there is to say about a driver with no bar: free since when, and where. */
export function Note({
  at,
  tone = 'neutral',
  top,
  className,
  children,
}: {
  at: number;
  tone?: Tone;
  /** Pixels from the top of the track. Defaults to 12, which centres it on a 40px row. */
  top?: number;
  className?: string;
  children?: ReactNode;
}) {
  const ink =
    tone === 'brand'
      ? 'text-brand'
      : tone === 'warn'
        ? 'text-warn'
        : tone === 'danger'
          ? 'text-danger'
          : tone === 'ok'
            ? 'text-ok'
            : tone === 'info'
              ? 'text-info'
              : 'text-ink-3';

  return (
    <span
      className={cx('absolute pl-1.5 text-[11.5px] whitespace-nowrap', ink, className)}
      style={{ left: `${at}%`, top: top ?? 12 }}
    >
      {children}
    </span>
  );
}

/** A moment rather than a stretch: the end of a shift, drawn as a 2px rule with its time. */
export function Marker({
  at,
  title,
  className,
}: {
  at: number;
  title?: string;
  className?: string;
}) {
  return (
    <span
      title={title}
      className={cx('absolute inset-y-0 w-0.5 -ml-px bg-danger', className)}
      style={{ left: `${at}%` }}
    />
  );
}
